import _ from "lodash";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createContext } from "use-context-selector";
import bluebird from "bluebird";
import type { ApolloClient } from "@apollo/client";
import fileUtils, {
  FileEntity,
  FileShareEntityType,
} from "../../../utils/fileUtils";

import {
  CreateTemporaryOpportunityAttachmentMutationResult,
  CreateTemporaryOpportunityAttachmentMutationVariables,
  CreateTemporaryOpportunityAttachmentQuery,
  GetFilesByEntityQuery,
  GetFilesByEntityQueryResult,
  GetFilesByEntityQueryVariables,
  GetProposalFilesByEntityQuery,
  GetProposalFilesByEntityQueryResult,
  GetProposalFilesByEntityQueryVariables,
  PfsFile,
  RemoveTemporaryOpportunityAttachmentMutationResult,
  RemoveTemporaryOpportunityAttachmentMutationVariables,
  RemoveTemporaryOpportunityAttachmentQuery,
  UnitOfMeasurement,
  UnlinkFilesFromEntityMutationResult,
  UnlinkFilesFromEntityMutationVariables,
  UnlinkFilesFromEntityQuery,
  UnsharePfsFileMutationResult,
  UnsharePfsFileMutationVariables,
  UnsharePfsFileQuery,
} from "../../../schema/generated/schema";
import { useClient, usePfsClient } from "../../../apollo";
import { useConfig } from "./configContext";

interface FileContextIntl {
  data: {
    getFileByEntityType: (type: FileShareEntityType) => PfsFile[];
    defaultShareEntity: FileEntity;
    files: PfsFile[];
    errMessage: string;
    loading: boolean;
    guestUpload?: boolean;
  };
  actions: {
    dismissErrors: () => void;
    removeFile: (
      entityId: string,
      entityType: string,
      fileId: string
    ) => Promise<void>;
    unshareFile: (fileId: string, user: string) => Promise<void>;
    addFiles: (
      filesToAdd: File[],
      fileEntity: FileEntity,
      userId: string,
      buyerUserId: string,
      sellerId: string,
      fileUnits?: Record<string, UnitOfMeasurement>
    ) => Promise<PfsFile[]>;
  };
}

type FileContextAction = FileContextIntl["actions"];
type FileContextData = FileContextIntl["data"];

interface ProviderProps {
  children: React.ReactElement | React.ReactElement[];
  entities: FileEntity[];
  defaultShareEntity: FileEntity;
  // eslint-disable-next-line react/require-default-props
  guestUpload?: boolean;
}

const FileContext = createContext<FileContextIntl>({
  data: {
    guestUpload: false,
    files: [],
    errMessage: "",
    loading: false,
    defaultShareEntity: {
      type: FileShareEntityType.PROPOSAL,
      id: "",
    },
    getFileByEntityType: () => [] as PfsFile[],
  },
  actions: {
    dismissErrors() {
      throw new Error("not implemented");
    },
    removeFile() {
      return Promise.reject(new Error("not implemented"));
    },
    unshareFile() {
      return Promise.reject(new Error("not implemented"));
    },
    addFiles() {
      return Promise.reject(new Error("not implemented"));
    },
  },
});

type entityFileMap = {
  [id: string]: PfsFile[];
};

const getFilesByEntity = async (
  client: ApolloClient<any>,
  pfsClient: ApolloClient<any>,
  entity: FileEntity
): Promise<PfsFile[]> => {
  let result;
  if (!entity.id) {
    return [];
  }
  if (entity.type !== FileShareEntityType.FORM) {
    result = await client.query<
      GetProposalFilesByEntityQueryResult,
      GetProposalFilesByEntityQueryVariables
    >({
      query: GetProposalFilesByEntityQuery,
      fetchPolicy: "no-cache",
      variables: {
        entityType: entity.type,
        entityId: entity.id,
      },
    });
    result = result.data.getProposalFilesByEntity;
  } else {
    result = await pfsClient.query<
      GetFilesByEntityQueryResult,
      GetFilesByEntityQueryVariables
    >({
      query: GetFilesByEntityQuery,
      fetchPolicy: "no-cache",
      variables: {
        entityType: entity.type,
        entityId: entity.id,
      },
    });
    result = result.data.getFilesByEntity;
  }
  return result;
};

export const FileContextProvider = ({
  children,
  entities,
  defaultShareEntity,
  guestUpload,
}: ProviderProps): React.ReactElement => {
  const client = useClient();
  const pfsClient = usePfsClient();
  const [loading, setLoading] = useState(false);
  const [errMessage, setErrMessage] = useState("");
  const [fileMap, setFileMap] = useState({} as entityFileMap);
  const filesCopy = useRef<PfsFile[]>([]);
  const { origins, authHeaders } = useConfig();

  const getAttachmentUploadUrl = async (
    opportunityId: string,
    fileName: string
  ): Promise<string> => {
    const resp = await client.mutate<
      CreateTemporaryOpportunityAttachmentMutationResult,
      CreateTemporaryOpportunityAttachmentMutationVariables
    >({
      mutation: CreateTemporaryOpportunityAttachmentQuery,
      variables: {
        opportunityId,
        fileName,
      },
    });

    const uploadUrl =
      resp.data?.createTemporaryOpportunityAttachment?.uploadUrl;
    if (!uploadUrl) {
      console.error(
        "graphql error for CreateTemporaryOpportunityAttachment",
        resp.errors
      );
      throw new Error("unable to create temporary upload url");
    }
    return uploadUrl;
  };

  const removeAttachment = useCallback(
    async (fileId: string): Promise<boolean> => {
      const key = `${defaultShareEntity.type}${defaultShareEntity.id}`;
      console.log(fileMap, key);
      const currentFiles = fileMap[key];
      if (!currentFiles?.find(i => i.id === fileId)) {
        throw new Error("file not found");
      }
      const resp = await client.mutate<
        RemoveTemporaryOpportunityAttachmentMutationResult,
        RemoveTemporaryOpportunityAttachmentMutationVariables
      >({
        mutation: RemoveTemporaryOpportunityAttachmentQuery,
        variables: {
          opportunityId: defaultShareEntity.id,
          fileId,
        },
      });

      const success = resp.data?.removeTemporaryOpportunityAttachment;

      if (!success) {
        console.error(
          "graphql error for RemoveTemporaryOpportunityAttachment",
          resp.errors
        );
        throw new Error("unable to remove attachment");
      }

      setFileMap({
        ...fileMap,
        [key]: currentFiles.filter(i => i.id !== fileId),
      });

      return success;
    },
    [fileMap, defaultShareEntity]
  );

  const refetch = useCallback(async () => {
    const result = {};
    if (guestUpload) {
      return result;
    }
    setLoading(true);
    try {
      await bluebird.map(entities, async entity => {
        result[entity.type + entity.id] = await getFilesByEntity(
          client,
          pfsClient,
          entity
        );
      });
    } catch (err: any) {
      console.error(err);
      setErrMessage(
        `There was a problem loading files: ${err.message || "Unknown"}`
      );
    }
    setFileMap(result);
    setLoading(false);
  }, [entities]);

  const entityKeys = entities.map(e => e.type + e.id).join(",");
  useEffect(() => {
    refetch();
  }, [entityKeys]);

  const files: PfsFile[] = useMemo(
    () =>
      _.chain(fileMap)
        .values()
        .flatten()
        .sortBy((file: PfsFile) => {
          const dateValue = file.createdAt
            ? new Date(file.createdAt).valueOf()
            : Date.now();
          return dateValue * -1;
        })
        .value(),
    [fileMap]
  );
  filesCopy.current = [...files];

  const getFileByEntityType = useCallback<
    FileContextData["getFileByEntityType"]
  >(type => {
    return filesCopy.current.filter(file => {
      return file.contextMeta.entityType === type.toString();
    });
  }, []);

  const dismissErrors = useCallback<FileContextAction["dismissErrors"]>(() => {
    setErrMessage("");
  }, []);

  const removeFile = useCallback<FileContextAction["removeFile"]>(
    async (entityId, entityType, fileId) => {
      try {
        if (guestUpload) {
          await removeAttachment(fileId);
        } else {
          await pfsClient.mutate<
            UnlinkFilesFromEntityMutationResult,
            UnlinkFilesFromEntityMutationVariables
          >({
            mutation: UnlinkFilesFromEntityQuery,
            variables: {
              entityId,
              entityType,
              fileIds: [fileId],
            },
          });
        }
      } catch (err: any) {
        console.error(err);
        setErrMessage(`There was an issue deleting the file: ${err.message}`);
        return;
      }
      refetch();
    },
    [refetch, removeAttachment]
  );

  const unshareFile = useCallback<FileContextAction["unshareFile"]>(
    async (fileId, user) => {
      try {
        await pfsClient.mutate<
          UnsharePfsFileMutationResult,
          UnsharePfsFileMutationVariables
        >({
          mutation: UnsharePfsFileQuery,
          variables: {
            fileId,
            user,
          },
        });
      } catch (err: any) {
        console.error(err);
        setErrMessage(`There was an issue deleting the file: ${err.message}`);
        return;
      }
      refetch();
    },
    [refetch]
  );

  const addFiles = useCallback<FileContextAction["addFiles"]>(
    async (
      filesToAdd,
      fileEntity,
      userId,
      buyerUserId,
      sellerId,
      fileUnits?
    ) => {
      let result;
      if (guestUpload) {
        const key = fileEntity.type + fileEntity.id;
        result = [];
        // eslint-disable-next-line no-restricted-syntax
        for (const file of filesToAdd) {
          // eslint-disable-next-line no-await-in-loop
          const uploadUrl = await getAttachmentUploadUrl(
            fileEntity.id,
            file.name
          );
          // eslint-disable-next-line no-await-in-loop
          const pfsFile = await fileUtils.uploadToUrl(uploadUrl, file);
          result.push(pfsFile);
        }
        setFileMap(_fileMap => {
          return { ..._fileMap, [key]: [...(_fileMap[key] || []), ...result] };
        });
        return result;
      }
      setLoading(true);
      try {
        result = await fileUtils.pfsUploadFiles(
          origins.partFilesService,
          filesToAdd,
          userId || "",
          {
            entityType: fileEntity.type,
            entityId: fileEntity.id,
            users: [
              { type: "XOMETRY_PARTNER", externalId: sellerId },
              { type: "XOMETRY", externalId: buyerUserId },
            ].filter(i => !!i.externalId),
          },
          fileUnits,
          authHeaders
        );
        await refetch();
      } catch (err: any) {
        console.error(err);
        setErrMessage(
          `There was a problem uploading files: ${err.message || "Unknown"}`
        );
      }
      setLoading(false);
      return result;
    },
    [refetch, origins]
  );

  const value: FileContextIntl = {
    data: {
      files,
      guestUpload,
      errMessage,
      loading,
      getFileByEntityType,
      defaultShareEntity,
    },
    actions: {
      dismissErrors,
      removeFile,
      unshareFile,
      addFiles,
    },
  };

  return <FileContext.Provider value={value}>{children}</FileContext.Provider>;
};

export default FileContext;
